import SearchField from "@/components/utils/SearchField";
import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { Debouncer } from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/utils/Debouncer";
import ChatUserDataDTO from "@/models/chat/ChatUserDataDTO";
import LazoUserService from "@/services/LazoUserService";
import { CloseOutlined } from "@mui/icons-material";
import { Box, Button, Divider, MenuItem, MenuList, Modal, Typography, styled } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import PortalUserItemComponent from "./PortalUserItemComponent";

const debouncer = new Debouncer(300);

type Props = {
  open: boolean;
  onClose: () => void;
  onStartPrivateChat: (user: ChatUserDataDTO, tenantExternalId: string) => void;
  onStartGroupChat: (users: string[], name: string) => void;
  creatingGroupChat: boolean;
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
};

export function PortalCreateChatModal(props: Props) {
  const [loadingUserChatData, setLoadingUserChatData] = useState<boolean>(false);
  const [inboxUserChatData, setInboxUserChatData] = useState<ChatUserDataDTO[]>([]);
  const [checkedUsers, setCheckedUsers] = useState<Record<string, ChatUserDataDTO>>({});

  const [openTenantSelectModal, setOpenTenantSelectModal] = useState<boolean>(false);

  const [selectedUserPrivateChat, setSelectedUserPrivateChat] = useState<ChatUserDataDTO>(null);

  const currentUserExternalId = AuthService.getUser().externalId;

  useEffect(() => {
    if (!props.open) {
      setInboxUserChatData([]);
      setCheckedUsers({});
    }
  }, [props.open]);

  function handleSearchPeople(searchRef: React.RefObject<HTMLInputElement>) {
    const lowerCaseSearchValue = searchRef.current?.value.toLowerCase().trim() || "";
    setLoadingUserChatData(true);
    LazoUserService.searchUsersForPortalChat(lowerCaseSearchValue || null)
      .then((r) => {
        r.onSuccess((c) => {
          setInboxUserChatData(c.filter((u) => u.externalId !== currentUserExternalId));
        });
        r.onError((m) => {
          NotificationService.error(m);
        });
      })
      .finally(() => setLoadingUserChatData(false));
  }

  function onClose() {
    setCheckedUsers({});
    setInboxUserChatData([]);
    props.onClose();
  }

  function onUserSelect(user: ChatUserDataDTO) {
    if (user.tenantNames.length > 1) {
      setSelectedUserPrivateChat(user);
      setOpenTenantSelectModal(true);
    } else {
      props.onStartPrivateChat(user, user.tenantExternalIds[0]);
    }
  }

  function onCloseTenantSelectModal() {
    setOpenTenantSelectModal(false);
    setSelectedUserPrivateChat(null);
  }

  function onClickTenant(tenantExternalId: string) {
    props.onStartPrivateChat(selectedUserPrivateChat, tenantExternalId);
    onCloseTenantSelectModal();
  }

  return (
    <>
      <Modal open={props.open} onClose={onClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ModalContent>
          <Box display="flex" flexDirection="column" gap="0.25rem" padding="0 0.375rem">
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight="600" fontSize="1rem">
                Create Chat
              </Typography>
              <Button
                onClick={props.onClose}
                disableRipple
                sx={{
                  minWidth: "fit-content",
                  borderRadius: "100px",
                  padding: "0.25rem",
                  ":hover": { backgroundColor: Colors.LIGHT_GRAY },
                }}
              >
                <CloseOutlined />
              </Button>
            </Box>
            <StyledDivider />
            <SearchField
              handleSearch={handleSearchPeople}
              displaySearchButton={false}
              debouncer={debouncer}
              triggerSearchOnInputChange
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  height: "2.5rem",
                  width: "100%",
                  //maxWidth: "30rem",
                  margin: "0.5rem 0",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "10px",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              minHeight: "89%",
              overflowY: "auto",
            }}
          >
            {loadingUserChatData ? (
              <Spinner sx={{ color: Colors.LAZO_PURPLE }} />
            ) : (
              <PortalUserItemComponent
                checkedUsers={checkedUsers}
                setCheckedUsers={setCheckedUsers}
                inboxUserChatData={inboxUserChatData}
                onUserSelect={onUserSelect}
                groupChat={props.creatingGroupChat}
                step={props.step}
                setStep={props.setStep}
                onCreateGroupChat={props.onStartGroupChat}
              />
            )}
          </Box>
        </ModalContent>
      </Modal>
      {openTenantSelectModal && (
        <Modal
          open={openTenantSelectModal}
          onClose={onCloseTenantSelectModal}
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <ModalContent sx={{ width: "30vw", height: "fit-content", maxHeight: "30vh" }}>
            <Box display="flex" flexDirection="column" gap="0.25rem" padding="0 0.375rem">
              <Box display="flex" justifyContent="space-between">
                <Typography fontWeight="600" fontSize="1rem">
                  Select Company
                </Typography>
                <Button
                  onClick={onCloseTenantSelectModal}
                  disableRipple
                  sx={{
                    minWidth: "fit-content",
                    borderRadius: "100px",
                    padding: "0.25rem",
                    ":hover": { backgroundColor: Colors.LIGHT_GRAY },
                  }}
                >
                  <CloseOutlined />
                </Button>
              </Box>
              <StyledDivider />
            </Box>
            <MenuList sx={{ overflowY: "auto" }}>
              {selectedUserPrivateChat.tenantNames.map((name, idx) => (
                <MenuItemContainer key={name + idx} onClick={() => onClickTenant(selectedUserPrivateChat.tenantExternalIds[idx])}>
                  <Typography>{name}</Typography>
                </MenuItemContainer>
              ))}
            </MenuList>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

const ModalContent = styled(Box)(() => ({
  position: "relative",
  gap: "0.25rem",
  flexDirection: "column",
  display: "flex",
  backgroundColor: "white",
  borderRadius: "0.5rem",
  padding: "0.375rem 0",
  height: "80vh",
  width: "40vw",
}));

const StyledDivider = styled(Divider)(() => ({
  width: "100%",
}));

const MenuItemContainer = styled(MenuItem)(() => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  justifyContent: "space-between",
  transition: "all 0.125s ease-out",
  backgroundColor: "transparent",
}));
